import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useParams } from 'react-router-dom'
import { Hand, Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import {
  guestLeave, guestMessages, guestNote, guestPostMessage, guestRaiseHand, guestSaveNote,
  guestStanding, guestToken, knock, previewMeeting,
} from './guestApi'
import { ChatPanel, NotesPanel } from './panels'
import { VideoGrid } from './VideoGrid'
import { connect, getSnapshot, leave, subscribe, toggleCamera, toggleMic } from './room'
import { whenLabel } from './vocab'
import type { GuestPreview, GuestStanding } from './types'

const POLL_MS = 3000

/**
 * Joining a meeting with a link and no account.
 *
 * This page sits OUTSIDE the shell and outside `RequireAuth`, deliberately: a guest is not a
 * staff user, holds a different kind of token on a different filter chain, and must never be
 * bounced to a sign-in screen. It guards itself — there is nothing here to guard but a link.
 *
 * Four states, and they are four different things to be told:
 *
 * - **the link is dead** — revoked, expired, or never real. The server answers all three the
 *   same way and so does this page; saying which would tell somebody guessing that they were
 *   getting warmer.
 * - **give your name** — the host admits people by name, so a blank one makes their queue
 *   unusable.
 * - **waiting** — polled. The grant arrives in the first answer after the chair admits you.
 * - **removed** — with the reason, because somebody put out of a meeting is entitled to it.
 */
export function GuestJoinPage() {
  const { token = '' } = useParams()
  const [preview, setPreview] = useState<GuestPreview | null>(null)
  const [standing, setStanding] = useState<GuestStanding | null>(null)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(true)

  const media = useSyncExternalStore(subscribe, getSnapshot)
  const connected = media.phase === 'connected'
  const [guestTab, setGuestTab] = useState<'chat' | 'notes'>('chat')
  /*
     Held locally, because the guest route answers 204 and `standing` does not carry it. That
     is the deliberate shape: a public link is handed no roster, so this page can show its own
     hand and nobody else's. The chair sees the queue; the guest sees that they are in it.
  */
  const [handUp, setHandUp] = useState(false)

  const chatIo = useMemo(() => ({ list: guestMessages, post: guestPostMessage }), [])
  const noteIo = useMemo(() => ({ load: guestNote, save: guestSaveNote }), [])

  useEffect(() => {
    let live = true
    previewMeeting(token)
      .then((p) => { if (live) setPreview(p) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setChecking(false) })
    return () => { live = false }
  }, [token])

  // A tab that already knocked keeps its place in the queue through a reload.
  useEffect(() => {
    if (!guestToken.get()) return
    guestStanding().then(setStanding).catch(() => guestToken.clear())
  }, [])

  const poll = useCallback(async () => {
    try {
      const next = await guestStanding()
      setStanding(next)
      if (next.grant && media.phase === 'idle') {
        await connect(next.grant, name || 'Guest')
      }
    } catch (e) {
      setError((e as Error).message)
    }
  }, [media.phase, name])

  useEffect(() => {
    if (!standing || standing.admissionCode !== 'WAITING') return
    const t = setInterval(() => { void poll() }, POLL_MS)
    return () => clearInterval(t)
  }, [standing, poll])

  useEffect(() => () => { void leave() }, [])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const knocked = await knock(token, name.trim())
      setStanding({
        admissionCode: knocked.admissionCode,
        meetingTitle: knocked.meetingTitle,
        meetingStatusCode: 'LIVE',
        removalReason: null,
        grant: null,
      })
      // With no lobby the server admits immediately, so ask once rather than waiting a poll.
      await poll()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (checking) return <Shell><LoadingBlock label="Checking the link…" /></Shell>

  if (!preview) {
    return (
      <Shell>
        <h1 className="text-erp-lg font-semibold">This link does not open a meeting</h1>
        <p className="mt-2 text-erp-sm text-muted-foreground">
          It may have been withdrawn, or it may have expired. Ask whoever invited you for a
          new one.
        </p>
      </Shell>
    )
  }

  if (standing?.admissionCode === 'REMOVED') {
    return (
      <Shell>
        <h1 className="text-erp-lg font-semibold">You were removed from this meeting</h1>
        {standing.removalReason && (
          <p className="mt-2 text-erp-sm text-muted-foreground">{standing.removalReason}</p>
        )}
      </Shell>
    )
  }

  if (standing?.admissionCode === 'DENIED') {
    return (
      <Shell>
        <h1 className="text-erp-lg font-semibold">You were not admitted</h1>
        <p className="mt-2 text-erp-sm text-muted-foreground">
          The chair did not let you in. Ask whoever invited you.
        </p>
      </Shell>
    )
  }

  if (connected) {
    return (
      <div className="min-h-dvh bg-background p-4">
        <h1 className="mb-3 text-erp-base font-semibold">{preview.title}</h1>
        {handUp && (
          <p className="mb-3 flex items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2
                        text-erp-sm text-amber-700">
            <Hand className="size-4 shrink-0" />
            Your hand is up. The chair can see where you are in the queue.
          </p>
        )}
        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          <div>
            <VideoGrid peers={media.peers} />
          </div>
          {/*
             A guest gets the same chat everybody else reads and the same private notepad.
             Their notes are theirs: the chair cannot read them either.
          */}
          <div className="flex h-[26rem] flex-col rounded-lg border border-border bg-card p-3">
            <div className="mb-2 flex gap-1 border-b border-border pb-2">
              {(['chat', 'notes'] as const).map((t) => (
                <button key={t} type="button"
                  className={`erp-btn h-7 px-2 text-erp-xs ${
                    guestTab === t ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
                  onClick={() => setGuestTab(t)}>
                  {t === 'chat' ? 'Chat' : 'Notes'}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1">
              {guestTab === 'chat' ? <ChatPanel io={chatIo} /> : <NotesPanel io={noteIo} />}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={() => void toggleMic()}>
            {media.micOn ? <Mic className="size-4" /> : <MicOff className="size-4 text-destructive" />}
            {media.micOn ? 'Mute' : 'Unmute'}
          </button>
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={() => void toggleCamera()}>
            {media.cameraOn ? <Video className="size-4" /> : <VideoOff className="size-4 text-destructive" />}
            {media.cameraOn ? 'Stop video' : 'Start video'}
          </button>
          <button type="button"
            className={`erp-btn h-9 text-erp-sm ${handUp ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
            onClick={() => {
              const next = !handUp
              guestRaiseHand(next)
                .then(() => setHandUp(next))
                .catch((e) => setError((e as Error).message))
            }}>
            <Hand className="size-4" /> {handUp ? 'Lower hand' : 'Raise hand'}
          </button>
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm text-destructive"
            onClick={() => { void leave(); void guestLeave() }}>
            <PhoneOff className="size-4" /> Leave
          </button>
        </div>
      </div>
    )
  }

  /*
    ADMITTED BUT NOT YET IN THE ROOM.

    Without this branch the render chain was `connected` → room, `WAITING` → lobby, and
    EVERYTHING ELSE → the join form. An admitted guest is none of the first two until the
    media connection reports back, so they were shown the form they had already filled in —
    the same screen whether they had never knocked or were seconds from being in. If the
    connection failed outright they stayed there forever with no reason given, while the
    chair's roster listed them as present.

    media.error is surfaced here and in the room, because it is the store that knows why a
    camera did not open; the page's own `error` only ever holds API failures, and showing
    just that one is what made a device refusal silent.
  */
  if (standing?.admissionCode === 'ADMITTED' && !connected) {
    const failed = media.phase === 'failed'
    return (
      <Shell>
        <h1 className="text-erp-lg font-semibold">{preview.title}</h1>
        <p className="mt-1 text-erp-sm text-muted-foreground">{whenLabel(preview.scheduledAt)}</p>
        <div className="mt-6 space-y-2">
          <p className="font-medium">
            {failed ? 'You are admitted, but the connection failed' : 'You are in — connecting…'}
          </p>
          <p className="text-erp-sm text-muted-foreground">
            {failed
              ? 'The chair has let you in. Rejoin to try the connection again.'
              : 'The chair has let you in. Opening the room.'}
          </p>
        </div>
        <ErrorStrip message={media.error ?? error} className="mt-4" />
        {failed && (
          <button type="button" className="erp-btn erp-btn-primary mt-4 h-10 w-full"
            onClick={() => { void poll() }}>
            Try again
          </button>
        )}
      </Shell>
    )
  }

  if (standing?.admissionCode === 'WAITING') {
    return (
      <Shell>
        <h1 className="text-erp-lg font-semibold">{preview.title}</h1>
        <p className="mt-1 text-erp-sm text-muted-foreground">{whenLabel(preview.scheduledAt)}</p>
        <div className="mt-6 space-y-2">
          <p className="font-medium">Waiting to be let in</p>
          <p className="text-erp-sm text-muted-foreground">
            The chair can see that you are here, under the name you gave. This page lets you
            in by itself the moment they admit you — there is nothing to press.
          </p>
        </div>
        <ErrorStrip message={error} className="mt-4" />
      </Shell>
    )
  }

  return (
    <Shell>
      <h1 className="text-erp-lg font-semibold">{preview.title}</h1>
      <p className="mt-1 text-erp-sm text-muted-foreground">{whenLabel(preview.scheduledAt)}</p>

      {/* media.error too: a refused camera is reported by the room store, not by the
          API call this page makes, and showing only the latter hid it entirely. */}
      <ErrorStrip message={error ?? media.error} className="mt-4" />

      <label className="mt-6 block">
        <span className="text-erp-sm font-medium">Your name</span>
        <span className="mt-1 block text-erp-xs text-muted-foreground">
          {preview.lobbyRequired
            ? 'The chair sees this when deciding whether to let you in.'
            : 'Shown to everybody in the meeting.'}
        </span>
        <input className="erp-input mt-2 h-10 w-full" value={name} autoFocus maxLength={120}
          placeholder="Jane Mwangi" onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim().length >= 2) void submit() }} />
      </label>

      <button type="button" className="erp-btn erp-btn-primary mt-4 h-10 w-full text-erp-sm"
        disabled={busy || name.trim().length < 2} onClick={() => void submit()}>
        {busy ? 'Asking to join…' : preview.lobbyRequired ? 'Ask to join' : 'Join'}
      </button>

      <p className="mt-3 text-erp-xs text-muted-foreground">
        Your camera and microphone are not opened until you are in the meeting.
      </p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
        {children}
      </div>
    </div>
  )
}
